import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Lock, FileText, Inbox, BarChart3, Clock, Plus, Search, Edit } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const SUGGESTIONS = [
  'Customer satisfaction survey',
  'Event registration flow',
  'Employee feedback form',
  'Waitlist for my new app',
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [intent, setIntent] = useState('');
  const [recentForms, setRecentForms] = useState<any[]>([]);
  const { canCreateForm, isLoading: isLimitsLoading, formsRemaining, formsLimit } = usePlanLimits();
  const { toast } = useToast();

  useEffect(() => {
    const fetchRecentForms = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('forms')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);
      if (data) setRecentForms(data);
    };
    fetchRecentForms();
  }, [user]);

  const handleBuild = () => {
    if (!canCreateForm) {
      toast({
        title: "Limit reached",
        description: `You've reached your limit of ${formsLimit} forms.`,
        variant: "destructive"
      });
      navigate('/billing');
      return;
    }

    if (intent.trim()) {
      navigate(`/ai-builder?intent=${encodeURIComponent(intent.trim())}`);
    } else {
      navigate('/ai-builder');
    }
  };

  const stats = [
    { label: 'Total Forms', value: '0', icon: FileText, color: 'text-blue-500' },
    { label: 'Submissions', value: '0', icon: Inbox, color: 'text-purple-500' },
    { label: 'Avg. Completion', value: '0%', icon: BarChart3, color: 'text-emerald-500' },
  ];

  return (
    <DashboardLayout>
      <Helmet>
        <title>Dashboard | Bravax Forms</title>
      </Helmet>

      <div className="space-y-12">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-display font-bold tracking-tight">
              Welcome back, {user?.name?.split(' ')[0] || 'User'}
            </h1>
            <p className="text-muted-foreground">
              Here is what's happening with your forms today.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
             <Button variant="outline" className="rounded-xl h-10 gap-2 flex-1 sm:flex-none" onClick={() => navigate('/forms')}>
                <Search className="h-4 w-4" />
                <span>Find Form</span>
             </Button>
             <Button className="rounded-xl h-10 gap-2 shadow-lg shadow-primary/20 flex-1 sm:flex-none" onClick={() => navigate('/form-builder')}>
                <Plus className="h-4 w-4" />
                <span>Blank Form</span>
             </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
           {stats.map((stat, i) => (
             <Card key={i} className={`border-border/40 bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-all ${i === 2 ? 'col-span-2 sm:col-span-1' : ''}`}>
                <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
                   <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-background border border-border/40 flex items-center justify-center shadow-inner flex-shrink-0")}>
                      <stat.icon className={cn("h-5 w-5 sm:h-6 sm:w-6", stat.color)} />
                   </div>
                   <div>
                      <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-widest">{stat.label}</p>
                      <p className="text-xl sm:text-2xl font-display font-bold">{stat.value}</p>
                   </div>
                </CardContent>
             </Card>
           ))}
        </div>

        {/* Quick Create Section */}
        <section className="relative">
           <div className="absolute -inset-1 bg-gradient-to-r from-primary/10 to-blue-500/10 blur-[40px] opacity-50" />
           <Card className="relative border-border/40 bg-card/80 backdrop-blur-md shadow-xl overflow-hidden rounded-3xl">
              <CardContent className="p-8 md:p-12">
                 <div className="max-w-3xl space-y-8">
                    <div className="space-y-2">
                       <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">
                          <Sparkles className="h-3 w-3" />
                          <span>AI Architect</span>
                       </div>
                       <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">What do you want to build?</h2>
                       <p className="text-muted-foreground">Describe your form in plain English. AI handles the structure, logic, and styling.</p>
                    </div>

                    <div className="relative group">
                       <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-500" />
                       <div className="relative">
                          <Textarea 
                            value={intent}
                            onChange={(e) => setIntent(e.target.value)}
                            placeholder="e.g. A registration form for my photography workshop next month..."
                            className="min-h-[140px] border-border/40 bg-background/50 rounded-2xl resize-none p-6 text-lg focus-visible:ring-primary/20 transition-all placeholder:text-muted-foreground/40"
                            disabled={!canCreateForm || isLimitsLoading}
                          />
                          <div className="absolute bottom-4 right-4">
                             <Button 
                               onClick={handleBuild}
                               disabled={isLimitsLoading}
                               className="bg-primary text-primary-foreground rounded-xl px-6 h-11 gap-2 shadow-lg shadow-primary/20"
                             >
                                <Sparkles className="h-4 w-4" />
                                <span>Build with AI</span>
                                <ArrowRight className="h-4 w-4" />
                             </Button>
                          </div>
                       </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                       <span className="text-xs text-muted-foreground font-medium mr-2">Suggestions:</span>
                       {SUGGESTIONS.map((s) => (
                         <button
                           key={s}
                           onClick={() => setIntent(s)}
                           className="px-3 py-1 text-xs rounded-full border border-border/40 bg-muted/20 hover:bg-primary/10 hover:border-primary/30 text-muted-foreground hover:text-primary transition-all"
                         >
                           {s}
                         </button>
                       ))}
                    </div>
                 </div>
              </CardContent>
           </Card>
        </section>

        {/* Recent Activity Section */}
        <section className="space-y-6">
           <div className="flex items-center justify-between">
              <h3 className="text-xl font-display font-bold tracking-tight">Recent Modules</h3>
              <Button variant="link" className="text-primary p-0 h-auto font-bold gap-1 group" onClick={() => navigate('/forms')}>
                 View all
                 <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </Button>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentForms.length > 0 ? (
                recentForms.map((form) => (
                  <Card key={form.id} className="group border-none bg-surface-low/50 hover:bg-surface-low backdrop-blur-xl transition-all duration-300 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl">
                    <CardContent className="p-6">
                       <div className="flex items-start justify-between mb-4">
                          <div className={cn(
                            "h-10 w-10 rounded-xl flex items-center justify-center",
                            form.is_published ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                          )}>
                             <FileText className="h-5 w-5" />
                          </div>
                          <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-widest bg-background/50">
                             {form.is_published ? 'Live' : 'Draft'}
                          </Badge>
                       </div>
                       <h4 className="font-bold tracking-tight mb-1 line-clamp-1 group-hover:text-primary transition-colors">{form.title}</h4>
                       <p className="text-xs text-muted-foreground line-clamp-2 mb-6 h-8">{form.description || "No description provided."}</p>
                       
                       <div className="flex items-center gap-3 pt-6 border-t border-border/5">
                           <Button 
                             size="sm" 
                             className="flex-[2] rounded-2xl h-10 px-4 text-[11px] font-bold uppercase tracking-widest btn-gradient gap-2"
                             onClick={() => navigate(`/form-builder/${form.id}`)}
                           >
                              <Edit className="h-3.5 w-3.5" />
                              Edit
                           </Button>
                           <Button 
                             variant="ghost" 
                             size="sm" 
                             className="flex-1 rounded-2xl h-10 px-3 text-[11px] font-bold uppercase tracking-widest border border-border/10 bg-surface-high/30 hover:bg-surface-high hover:text-primary transition-all duration-300 gap-2"
                             onClick={() => navigate(`/forms/${form.id}/submissions`)}
                           >
                              <BarChart3 className="h-3.5 w-3.5" />
                              Results
                           </Button>
                           <Button 
                             variant="ghost" 
                             size="icon"
                             className="rounded-2xl h-10 w-10 border border-border/10 bg-surface-high/30 hover:bg-surface-high hover:text-primary transition-all duration-300"
                             onClick={() => window.open(`${window.location.origin}/form/${form.id}`, '_blank')}
                             title="View Live Form"
                           >
                              <ArrowRight className="h-4 w-4" />
                           </Button>
                        </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-20 border-2 border-dashed border-border/10 rounded-3xl bg-surface-low/30">
                   <div className="w-16 h-16 rounded-2xl bg-surface-high flex items-center justify-center mb-4 shadow-inner">
                      <Clock className="h-8 w-8 text-muted-foreground/20" />
                   </div>
                   <h4 className="text-lg font-bold mb-1">No forms yet</h4>
                   <p className="text-muted-foreground text-sm mb-6">Start by creating your first form with AI above.</p>
                   {!canCreateForm && (
                     <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-destructive/10 text-destructive text-sm font-medium">
                        <Lock className="h-4 w-4" />
                        <span>Upgrade to create more forms</span>
                     </div>
                   )}
                </div>
              )}
           </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
